import React, {
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Control, FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getSnapshot, isStateTreeNode, onPatch } from 'mobx-state-tree';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { get, omit } from 'lodash-es';

import {
  Schema,
  useStore,
  Tokens,
  TokensProvider,
  YupAdapter,
} from '@mcn-platform/models';
import Tabs from 'components/Tabs';
import VerticalTabs from 'components/VerticalTabs';
import StandardField from '../forms/StandardField';
import StandardCheckboxField from '../forms/StandardCheckboxField';
import AutoUpdate from './AutoUpdate';
import {
  ItemEditSectionCollapseProvider,
  ItemEditSectionSearchBar,
  sectionPathToDomId,
  useSectionCollapse,
} from './itemEditSectionCollapse';

let FormElement:any;

type FieldProps = {
  control: Control;
  modelName: string;
  fieldName: string,
  overrides?: Record<string, any>;
  itemType: string;
  readOnly: boolean;
};

export const Field = ({
  control,
  fieldName,
  modelName,
  overrides,
  itemType,
  readOnly,
}:FieldProps) => {
  const field = Schema.getProperty(modelName, fieldName);
  const override = (overrides && overrides[fieldName]);
  if (override) {
    return override(control, fieldName, field, readOnly, itemType);
  }
  return (
    <StandardField
      control={control}
      fieldName={fieldName}
      field={field}
      itemType={itemType}
      readOnly={readOnly}
    />
  );
};

Field.defaultProps = {
  overrides: undefined,
};

/**
 * Sample config:
 *  {
 *    type: 'twoColumn',
 *    elements: ['registrationMax', 'registrationCount'],
 *  },
 * @param param0
 * @returns
 */
export const TwoColumn = ({
  formConfig,
  modelName,
  control,
  itemType,
  components,
  overrides,
  readOnly,
  pathPrefix,
  item,
}: any) => {
  const elements = formConfig.elements // defined directly in formConfig
    || Schema.getPropertyGroups(modelName)[formConfig.name] // or pulled from group property
    || []; // or empty
  const columnPathPrefix = pathPrefix !== undefined && pathPrefix !== null
    ? `${pathPrefix}.${formConfig.name || 'twoColumn'}`
    : undefined;
  return (
    <Grid
      container
      display="flex"
      flexWrap="nowrap"
      spacing={3}
    >
      {elements.map((element) => (
        <Grid
          item
          key={element.name || element}
          xs={12}
          md={6}
        >
          <FormElement
            control={control}
            formElement={element}
            itemType={itemType}
            overrides={overrides}
            components={components}
            modelName={modelName}
            readOnly={readOnly}
            pathPrefix={columnPathPrefix}
            item={item}
          />
        </Grid>
      ))}
    </Grid>
  );
};

/**
 * Sample config:
 * {
 *   type: 'guardedOption',
 *   controlledBy: 'registrationRequired',
 *   controlledField: 'registerBy',
 *   label: 'Registration Required',
 * },
 * @param param0
 * @returns
 */
export const GuardedOption = observer(({
  formConfig,
  modelName,
  control,
  itemType,
  // overrides,
  readOnly,
}: any) => {
  const controlChecked = useWatch({ control, name: formConfig.controlledBy });
  const controlledFieldReadOnly = readOnly || !controlChecked;
  const controlledField = Schema.getProperty(modelName, formConfig.controlledField);
  return (
    <Grid
      container
      direction="row"
      spacing={3}
    >
      <Grid
        key={`label-${formConfig.name}-guard`}
        item
        justifyContent="space-between"
        xs={3}
      >
        <Stack direction="column" spacing={1}>
          <FormLabel id={`label-${formConfig.name}`}>{formConfig.label}</FormLabel>
          <StandardCheckboxField
            control={control}
            fieldName={formConfig.controlledBy}
            field={{ ...formConfig, showLabel: false }}
            readOnly={readOnly}
          />
        </Stack>
      </Grid>
      <Grid
        key={`label-${formConfig.name}-field`}
        item
        justifyContent="flex-start"
        flexGrow={1}
        xs={9}
      >
        <StandardField
          control={control}
          fieldName={formConfig.controlledField}
          field={controlledField}
          // field={{ ...controlledField, showLabel: false }}
          itemType={itemType}
          readOnly={controlledFieldReadOnly}
        />
      </Grid>
    </Grid>
  );
});

export const FormSubGroup = ({
  modelName,
  control,
  formConfig,
  itemType,
  overrides,
  components,
  readOnly,
  item,
  sectionPath,
}: any) => {
  const sectionCtx = useSectionCollapse();
  const elements = formConfig.elements // defined directly in formConfig
    || Schema.getPropertyGroups(modelName)[formConfig.name] // or pulled from group property
    || []; // or empty

  const useCollapsibleSections = Boolean(
    sectionCtx && sectionPath && sectionCtx.isCollapsiblePath(sectionPath),
  );

  const fieldsGrid = (
    <Grid
      container
      direction="row"
      spacing={3}
    >
      {elements.map((element) => (
        <Grid
          key={element.name || element}
          item
          xs={12}
          justifyContent="space-between"
        >
          <FormElement
            control={control}
            formElement={element}
            itemType={itemType}
            overrides={overrides}
            components={components}
            modelName={modelName}
            readOnly={readOnly}
            item={item}
          />
        </Grid>
      ))}
    </Grid>
  );

  if (useCollapsibleSections && sectionPath) {
    const expanded = sectionCtx!.isSectionExpanded(sectionPath);
    return (
      <Grid
        id={sectionPathToDomId(sectionPath)}
        container
        direction="row"
        spacing={1}
      >
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              size="small"
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse section' : 'Expand section'}
              onClick={() => sectionCtx!.onSectionToggle(sectionPath, !expanded)}
              sx={{
                color: 'text.secondary',
                '& .MuiSvgIcon-root': {
                  transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: (theme) => theme.transitions.create('transform'),
                },
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
            <Typography variant="h4" component="div">
              {formConfig.label}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit={false}
            sx={{
              '&.MuiCollapse-entered': {
                overflow: 'visible',
              },
              '& .MuiCollapse-wrapperInner': {
                overflow: 'visible',
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                overflow: 'visible',
                border: '1px solid',
                borderColor: '#7f7f7f',
                borderRadius: 1,
                padding: 2,
              }}
            >
              {fieldsGrid}
            </Box>
          </Collapse>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      direction="row"
      spacing={1}
    >
      <Grid
        key={`label-${formConfig.name}-label`}
        item
        xs={12}
        justifyContent="space-between"
      >
        <Typography variant="h4" component="div">
          {formConfig.label}
        </Typography>
      </Grid>
      <Grid
        key={`label-${formConfig.name}-field`}
        item
        xs={12}
        justifyContent="space-between"
      >
        <Box
          sx={{
            width: '100%',
            border: '1px solid',
            borderColor: '#7f7f7f',
            borderRadius: 1,
            padding: 2,
          }}
        >
          {fieldsGrid}
        </Box>
      </Grid>
    </Grid>
  );
};

export const FormGroup = ({
  control,
  formConfig,
  modelName,
  itemType,
  overrides,
  components,
  readOnly,
  item,
  pathPrefix,
}:any) => {
  const elements = formConfig.elements // defined directly in formConfig
    || Schema.getPropertyGroups(modelName)[formConfig.name] // or pulled from group property
    || []; // or empty
  return (
    <Grid
      container
      display="flex"
      flexDirection="column"
      spacing={3}
      // sx={{ height: '100%' }}
      // sx={{ border: '1px solid black' }}
    >
      {elements.map((element) => (
        <Grid item key={element.name || element} flexGrow={1}>
          <FormElement
            control={control}
            formElement={element}
            itemType={itemType}
            overrides={overrides}
            components={components}
            modelName={modelName}
            readOnly={readOnly}
            item={item}
            pathPrefix={pathPrefix}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const TabsEditor = ({
  control,
  formElement,
  modelName,
  itemType,
  overrides,
  components,
  readOnly,
  item,
  collapsibleSections,
  collapsibleSectionsTabKeys,
}:any) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabName = formElement.name || 'tab';

  const elements = formElement.elements // defined directly in formConfig
    || Schema.getPropertyGroups(modelName)[formElement.name] // or pulled from group property
    || []; // or empty

  const setSelectedTab = (newTab:string) => {
    searchParams.set(tabName, newTab);
    setSearchParams(searchParams);
  };

  const tabDefinitions = elements.map((tab) => {
    const showSectionSearch = collapsibleSections && (
      !collapsibleSectionsTabKeys?.length || collapsibleSectionsTabKeys.includes(tab.name)
    );
    return {
      key: tab.name,
      label: tab.label,
      content: (
        <>
          {showSectionSearch ? <ItemEditSectionSearchBar tabKey={tab.name} /> : null}
          <FormElement
            control={control}
            formElement={tab}
            itemType={itemType}
            overrides={overrides}
            components={components}
            modelName={modelName}
            readOnly={readOnly}
            item={item}
            pathPrefix={tab.name}
          />
        </>
      ),
    };
  });
  if (formElement.subtype === 'vertical') {
    return (<VerticalTabs tabs={tabDefinitions} />);
  }

  return (
    <Tabs
      tabs={tabDefinitions}
      selected={searchParams.get(tabName) || ''}
      setSelected={setSelectedTab}
    />
  );
};

FormElement = ({
  formElement,
  control,
  itemType,
  overrides,
  components,
  modelName,
  readOnly,
  item,
  pathPrefix,
  collapsibleSections,
  collapsibleSectionsTabKeys,
}:any /* {formElement: FormGroup | FormSubGroup | string } */) => {
  // console.log('FormElement', formElement);
  if (typeof formElement === 'string') {
    return (
      <Field
        control={control}
        fieldName={formElement}
        itemType={itemType}
        modelName={modelName}
        overrides={overrides}
        readOnly={readOnly}
      />
    );
  }

  if (['string', 'number', 'fontFamily'].includes(formElement.type)) {
    return (
      <StandardField
        control={control}
        fieldName={formElement.name}
        field={formElement}
        itemType={itemType}
        readOnly={readOnly}
      />
    );
  }

  if (formElement.type === 'tabs') {
    return (
      <TabsEditor
        control={control}
        itemType={itemType}
        formElement={formElement}
        modelName={modelName}
        overrides={overrides}
        components={components}
        readOnly={readOnly}
        item={item}
        collapsibleSections={collapsibleSections}
        collapsibleSectionsTabKeys={collapsibleSectionsTabKeys}
      />
    );
  }

  if (formElement.type === 'group') {
    return (
      <FormGroup
        control={control}
        formConfig={formElement}
        itemType={itemType}
        overrides={overrides}
        components={components}
        modelName={modelName}
        readOnly={readOnly}
        item={item}
        pathPrefix={pathPrefix}
      />
    );
  }

  if (formElement.type === 'subGroup') {
    let sectionPath: string | undefined;
    if (pathPrefix !== undefined && pathPrefix !== null) {
      sectionPath = pathPrefix === '' ? formElement.name : `${pathPrefix}.${formElement.name}`;
    }
    return (
      <FormSubGroup
        control={control}
        formConfig={formElement}
        itemType={itemType}
        overrides={overrides}
        components={components}
        modelName={modelName}
        readOnly={readOnly}
        item={item}
        sectionPath={sectionPath}
      />
    );
  }

  if (formElement.type === 'component') {
    const component = components[formElement.name];
    if (component) {
      return React.isValidElement(component)
        ? component
        : React.createElement(component as React.ComponentType<{ modelName?: string; control?: Control; item?: unknown }>, {
          modelName,
          control,
          item,
        });
    }
    console.warn('No component with name found', formElement.name);
    return null;
  }

  if (formElement.type === 'guardedOption') {
    return (
      <GuardedOption
        control={control}
        formConfig={formElement}
        itemType={itemType}
        overrides={overrides}
        components={components}
        modelName={modelName}
        readOnly={readOnly}
      />
    );
  }
  if (formElement.type === 'twoColumn') {
    return (
      <TwoColumn
        control={control}
        formConfig={formElement}
        itemType={itemType}
        overrides={overrides}
        components={components}
        modelName={modelName}
        readOnly={readOnly}
        pathPrefix={pathPrefix}
        item={item}
      />
    );
  }

  console.log('Dont know what to do with', formElement);
  return null;
};
/**
 * If item.id is null then its a new item, otherwise updating.
 * @param
 * @returns
 */
const HookedForm = observer(({
  modelName,
  handleSubmit,
  formConfig,
  overrides,
  components,
  hideTitle,
  autoSave,
  itemType,
  isNew,
  item,
  validationSchema,
  readOnly,
  extraActions,
  collapsibleSections,
  collapsibleSectionsTabKeys,
  formValues, onUserFieldChange,
}:{
  modelName: string,
  formConfig: any,
  handleSubmit: any,
  overrides: Record<string, any>,
  components: Record<string, any>,
  hideTitle: boolean,
  autoSave: boolean,
  itemType: string,
  isNew: boolean,
  item: any,
  validationSchema: any,
  readOnly: boolean,
  extraActions?: React.ReactNode,
  collapsibleSections: boolean,
  collapsibleSectionsTabKeys?: string[],
  formValues?: any;
  onUserFieldChange?: (name: string, value: any) => boolean;
}) => {
  const model = Schema.getModel(modelName);
  const store = useStore();

  const initialValues = useMemo(() => omit(
    formValues || (isStateTreeNode(item) ? getSnapshot(item) : item),
    ['changeCount', 'syncState', 'updatedAt'],
  ), [item, formValues]);

  // console.log('formConfig: ', formConfig);
  console.log('initialValues: ', initialValues);
  // console.log('initial UpdatedAt', item.updatedAt);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: initialValues,
    // @ts-expect-error resolver mismatch
    resolver: yupResolver(validationSchema),
  });
  const {
    control, handleSubmit: innerHandleSubmit, watch,
    reset, setValue,
  } = methods;

  useEffect(() => {
    console.log('hooked form useEffect');

    // onPatch is triggered when item is updated, for example
    // from AI wizard.  This will update the form fields.
    if (isStateTreeNode(item)) {
      onPatch(item, (patch) => {
        if (typeof patch.value === 'string') {
          // console.info('------> Got change: ', patch);
          setValue(patch.path.substring(1), patch.value);
        }
      });
    }

    // if the item changes then update the fields.
    reset(omit(
      formValues || (isStateTreeNode(item) ? getSnapshot(item) : item),
      ['changeCount', 'syncState', 'updatedAt'],
    ));

    const subscription = watch((value, { name, type }) => {
      console.log('autoSave:', {
        item, value, name, autoSave,
      });

      // update the name of the section to match the category
      if (modelName === 'learningBoardSection' && name === 'category') {
        const selectedCategory = store.currentPortal?.getCategory(value.category);
        setValue('name', selectedCategory?.name || '');
      }

      const handled = name && type === 'change' && onUserFieldChange?.(name, get(value, name));
      if (autoSave && !handled) {
        item.updateFields([name], value, store);
        if (name === 'item.course' || name === 'item.microcast') {
          reset(item);
        }
      }
      // console.log('Updated value', item);
    });

    return () => subscription.unsubscribe();
  }, [store, setValue, reset, watch, item, onUserFieldChange ? undefined : item?.updatedAt, autoSave, modelName, formValues, onUserFieldChange]);

  const tabsUrlParam = formConfig?.[0]?.type === 'tabs'
    ? (formConfig[0].name || 'tab')
    : null;

  const cardBody = (
    <Card sx={{ height: '100%', overflow: 'auto' }}>
      {!hideTitle && (
        <>
          <CardHeader
            title={model.name}
          />
          <Divider />
        </>
      )}
      <CardContent sx={{ height: '100%' }}>
        <FormProvider {...methods}>
          <form
            style={{ height: '100%' }}
            onSubmit={(event) => {
              event.preventDefault();
              console.log('onSubmit', event);
              // innerHandleSubmit(handleSubmit)();
              innerHandleSubmit(handleSubmit, (errors) => console.log('ERRORS', errors))();
            }}
            // onChange={(event) => handleChange(event, getValues())}
          >
            <Grid
            container
            display="flex"
            flexDirection="column"
            spacing={3}
            sx={{ pb: 4 }}
            // sx={{ height: '100%' }}
            >
            {formConfig.map((element:any) => (
              <Grid key={element.name || element} item flexGrow={1}>
                <FormElement
                  key={element.name || element}
                  control={control}
                  formElement={element}
                  modelName={modelName}
                  itemType={itemType}
                  overrides={overrides}
                  components={components}
                  item={item}
                  readOnly={readOnly}
                  collapsibleSections={collapsibleSections}
                  collapsibleSectionsTabKeys={collapsibleSectionsTabKeys}
                />
              </Grid>
            ))}
          </Grid>
          {autoSave
            ? <AutoUpdate control={control} />
            : (
              <>
                <Divider />
                <Stack
                  alignContent="center"
                  justifyContent="right"
                  direction="row"
                  spacing={2}
                  sx={{
                    padding: 1,
                    textAlign: 'center',
                    height: '64px',
                  }}
                >
                  {extraActions}
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={item.changeCount === 0}
                  >
                    { isNew ? 'Create' : 'Save' }
                  </Button>
                </Stack>
              </>
            )}
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );

  if (collapsibleSections) {
    return (
      <ItemEditSectionCollapseProvider
        modelName={modelName}
        formConfig={formConfig}
        tabsUrlParam={tabsUrlParam}
        collapsibleTabKeys={collapsibleSectionsTabKeys}
      >
        {cardBody}
      </ItemEditSectionCollapseProvider>
    );
  }

  return cardBody;
});

interface ItemEditProps {
  modelName: string;
  item: any;
  handleSubmit: (values:any) => void;
  formConfig?: any | undefined;
  overrides?: Record<string, any>;
  components?: Record<string, any>;
  hideTitle?: boolean;
  autoSave?: boolean;
  readOnly?: boolean;
  /** Rendered in the bottom action bar next to the Save button */
  extraActions?: React.ReactNode;
  /**
   * When true, schema subGroups render as collapsed accordions by default, with a section search bar.
   * Opt in per screen — default off for existing ItemEdit usages.
   */
  collapsibleSections?: boolean;
  /**
   * When set (non-empty), only tabs whose config `name` is listed get collapse/search subsection UI.
   * Omit for all tabs. Example: `['general', 'tools']`.
   */
  collapsibleSectionsTabKeys?: string[];
  formValues?: any;
  onUserFieldChange?: (name: string, value: any) => boolean;
}

/**
 * If item.id is null then its a new item, otherwise updating.
 * @param
 * @returns
 */
const ItemEdit = ({
  modelName,
  item,
  handleSubmit,
  formConfig,
  components = {},
  overrides = {},
  hideTitle,
  autoSave,
  readOnly,
  extraActions,
  collapsibleSections,
  collapsibleSectionsTabKeys,
  formValues, onUserFieldChange,
}:ItemEditProps) => {
  // if (initialValues === undefined) {
  //   return <div>Loading...</div>;
  // }
  const validator = useMemo(() => YupAdapter(modelName), [modelName]);
  const { currentPortal } = useStore();
  // console.log('Model validator is', validator);
  return (
    <TokensProvider value={new Tokens(currentPortal)}>
      <HookedForm
        modelName={modelName}
        handleSubmit={handleSubmit}
        formConfig={formConfig || Schema.getFormConfig(modelName)}
        overrides={overrides}
        components={components}
        hideTitle={hideTitle || false}
        autoSave={autoSave || false}
        itemType={item.type}
        isNew={!item.id}
        item={item}
        validationSchema={validator}
        readOnly={!!readOnly}
        extraActions={extraActions}
        collapsibleSections={collapsibleSections || false}
        collapsibleSectionsTabKeys={collapsibleSectionsTabKeys}
        formValues={formValues}
        onUserFieldChange={onUserFieldChange}
      />
    </TokensProvider>
  );
};

ItemEdit.defaultProps = {
  formConfig: undefined,
  overrides: {},
  components: {},
  hideTitle: false,
  autoSave: false,
  readOnly: false,
  extraActions: undefined,
  collapsibleSections: false,
};

export default ItemEdit;
